import apiClient from "./client";

/**
 * TRONC Records API Service
 */
export const troncRecordApi = {
  /**
   * Get all TRONC records with optional filters
   * @param {Object} params - Query parameters
   * @param {string} params.fromDate - Start date (ISO format)
   * @param {string} params.toDate - End date (ISO format)
   * @param {string} params.locationId - Filter by location ID
   * @param {string} params.tenantId - Filter by tenant ID (for Global Admins)
   * @param {boolean} params.unsyncedOnly - Filter to unsynced records only
   */
  getAll: (params = {}) => {
    return apiClient.get("/troncrecords", params);
  },

  /**
   * Get TRONC record by ID
   * @param {string} id - TRONC record ID (GUID)
   */
  getById: (id) => {
    return apiClient.get(`/troncrecords/${id}`);
  },

  /**
   * Create a new TRONC record
   * @param {Object} record - TRONC record data
   */
  create: (record) => {
    return apiClient.post("/troncrecords", record);
  },

  /**
   * Update a TRONC record
   * @param {string} id - TRONC record ID
   * @param {Object} record - Updated TRONC record data
   */
  update: (id, record) => {
    return apiClient.put(`/troncrecords/${id}`, record);
  },

  /**
   * Update TRONC record status
   * @param {string} id - TRONC record ID
   * @param {Object} data - Status update data { status, notes }
   */
  updateStatus: (id, data) => {
    return apiClient.patch(`/troncrecords/${id}/status`, data);
  },

  /**
   * Delete a TRONC record
   * @param {string} id - TRONC record ID
   * @param {string} reason - Reason for deletion
   */
  delete: (id, reason = null) => {
    return apiClient.delete(`/troncrecords/${id}`, reason ? { reason } : {});
  },

  /**
   * Sync a TRONC record to Sona
   * @param {string} id - TRONC record ID
   */
  syncToSona: (id) => {
    return apiClient.post(`/troncrecords/${id}/sync`);
  },

  /**
   * Get TRONC summary by location
   * @param {Object} params - Query parameters
   * @param {string} params.tenantId - Filter by tenant ID (for Global Admins)
   */
  getSummaryByLocation: (params = {}) => {
    return apiClient.get("/troncrecords/summary/by-location", params);
  },

  /**
   * Get TRONC summary by month
   * @param {Object} params - Query parameters
   * @param {number} params.year - Optional year filter
   * @param {string} params.tenantId - Filter by tenant ID (for Global Admins)
   */
  getSummaryByMonth: (params = {}) => {
    return apiClient.get("/troncrecords/summary/by-month", params);
  },

  /**
   * Get total TRONC amount for a date range
   * @param {Object} params - Query parameters
   * @param {string} params.fromDate - Start date (ISO format)
   * @param {string} params.toDate - End date (ISO format)
   * @param {string} params.locationId - Optional location ID filter
   * @param {string} params.tenantId - Filter by tenant ID (for Global Admins)
   */
  getTotal: (params = {}) => {
    return apiClient.get("/troncrecords/total", params);
  },

  /**
   * Get locations that have TRONC records
   * @param {Object} params - Query parameters
   * @param {string} params.tenantId - Filter by tenant ID (for Global Admins)
   */
  getLocations: (params = {}) => {
    return apiClient.get("/troncrecords/locations", params);
  },

  /**
   * Get pending (undistributed) TRONC records
   * @param {Object} params - Query parameters
   */
  getPending: (params = {}) => {
    return apiClient.get("/troncrecords/pending", params);
  },

  /**
   * Get historical comparison data
   * @param {Object} params - Query parameters
   */
  getHistorical: (params = {}) => {
    return apiClient.get("/troncrecords/historical", params);
  },
};

export default troncRecordApi;
